import { useState, useEffect, useRef } from 'react';
import { Modal, Button, ProgressBar, Row, Col, Badge, Form } from 'react-bootstrap';
import { useApi } from '../../contexts/ApiProvider';
import { decimalToFraction } from '../../utils/mathUtils';

const formatTime = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
};

const playTimerChime = () => {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const ctx = new AudioContext();
        const playBeep = (startTime) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.setValueAtTime(880, startTime);
            gain.gain.setValueAtTime(1, startTime);
            gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.5);
            osc.start(startTime);
            osc.stop(startTime + 0.5);
        };
        playBeep(ctx.currentTime);
        playBeep(ctx.currentTime + 0.2);
    } catch (e) {
        console.error("Audio playback failed", e);
    }
};

export default function CookingModeModal({ show, onHide, recipe }) {
    const api = useApi();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [checkedItems, setCheckedItems] = useState(new Set());

    const [swaps, setSwaps] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [trialData, setTrialData] = useState({ rating: 5, notes: '', tweaks: '', image: null });

    const [activeTimers, setActiveTimers] = useState([]);
    const activeTimersRef = useRef(activeTimers);

    useEffect(() => {
        activeTimersRef.current = activeTimers;
    }, [activeTimers]);

    // --- UPGRADED TICKER ENGINE (ABSOLUTE TIME) ---
    useEffect(() => {
        if (!show) return;
        let tickCount = 0;

        // We tick every 500ms just to make sure the UI feels responsive
        const interval = setInterval(() => {
            tickCount++;
            setActiveTimers(prev => {
                let chimePlayed = false;

                return prev.map(t => {
                    if (t.isRunning && t.remaining > 0) {
                        // Calculate remaining time based on the absolute End Time vs Now
                        const nextRemaining = Math.max(0, Math.ceil((t.endTime - Date.now()) / 1000));

                        if (nextRemaining === 0) {
                            if (!chimePlayed) { playTimerChime(); chimePlayed = true; }
                            return { ...t, remaining: 0, isRunning: false, isRinging: true };
                        }
                        return { ...t, remaining: nextRemaining };
                    }

                    // Ringing loop
                    if (t.isRinging && tickCount % 6 === 0) { // Every 3 seconds (6 ticks of 500ms)
                        if (!chimePlayed) { playTimerChime(); chimePlayed = true; }
                    }
                    return t;
                });
            });
        }, 500);

        return () => clearInterval(interval);
    }, [show]);

    useEffect(() => {
        if (show) {
            setCurrentIndex(0);
            setCheckedItems(new Set());
            setActiveTimers([]);
            setSwaps({});
            setTrialData({ rating: 5, notes: '', tweaks: '', image: null });
        }
    }, [show]);

    if (!show || !recipe) return null;

    const totalPages = (recipe.steps?.length || 0) + 2;
    const progress = (currentIndex / (totalPages - 1)) * 100;

    const handleNext = () => { if (currentIndex < totalPages - 1) setCurrentIndex(prev => prev + 1); };
    const handlePrev = () => { if (currentIndex > 0) setCurrentIndex(prev => prev - 1); };

    const getActiveIngredient = (ing) => swaps[ing.id] || ing;

    const getIngredientDetails = (id) => {
        const baseIng = recipe.ingredients?.find(ing => ing.id === id || ing.client_id === id);
        if (!baseIng) return null;
        return getActiveIngredient(baseIng);
    };

    const getEquipmentDetails = (id) => recipe.equipment?.find(eq => eq.id === id || eq.client_id === id);
    const getComponentDetails = (id) => {
        for (const step of (recipe.steps || [])) {
            const out = (step.outputs || []).find(o => o.id === id || o.client_id === id);
            if (out) return out;
        }
        return null;
    };

    const extractTimeSuggestions = (text) => {
        if (!text) return [];
        const regex = /(\d+(?:\.\d+)?)\s*(hour|hours|hr|hrs|minute|minutes|min|mins|second|seconds|sec|secs)\b/gi;
        const matches = [...text.matchAll(regex)];
        return matches.map(match => {
            const val = parseFloat(match[1]);
            const unit = match[2].toLowerCase();
            let secs = 0;
            if (unit.startsWith('h')) secs = val * 3600;
            else if (unit.startsWith('m')) secs = val * 60;
            else if (unit.startsWith('s')) secs = val;
            return { label: match[0], totalSeconds: secs };
        });
    };

    // --- UPGRADED TIMER CREATION ---
    const startNewTimer = (label, totalSeconds) => {
        const newTimer = {
            id: `timer_${Date.now()}_${Math.random().toString(36).substr(2,5)}`,
            label,
            totalSeconds,
            remaining: totalSeconds,
            endTime: Date.now() + (totalSeconds * 1000), // Anchor to absolute time
            isRunning: true,
            isRinging: false,
            stepIndex: currentIndex
        };
        setActiveTimers(prev => [...prev, newTimer]);
    };

    // --- UPGRADED TIMER TOGGLE ---
    const toggleTimer = (id) => {
        setActiveTimers(prev => prev.map(t => {
            if (t.id === id) {
                if (t.isRinging) return { ...t, isRinging: false };
                if (t.remaining === 0) {
                    // Restart
                    return { ...t, remaining: t.totalSeconds, endTime: Date.now() + (t.totalSeconds * 1000), isRunning: true };
                }

                if (t.isRunning) {
                    // Pausing: Hold onto the current remaining time
                    return { ...t, isRunning: false };
                } else {
                    // Resuming: Create a new anchor point based on what was remaining
                    return { ...t, isRunning: true, endTime: Date.now() + (t.remaining * 1000) };
                }
            }
            return t;
        }));
    };

    const dismissTimer = (id) => setActiveTimers(prev => prev.filter(t => t.id !== id));

    const handleSaveTrial = async () => {
        setIsSaving(true);
        const form = new FormData();
        form.append('rating', trialData.rating);
        form.append('notes', trialData.notes);

        let automaticTweaks = [];
        Object.keys(swaps).forEach(originalId => {
            const originalIng = recipe.ingredients.find(i => i.id.toString() === originalId.toString());
            const swappedFor = swaps[originalId];
            if (originalIng && swappedFor) {
                automaticTweaks.push(`Swapped ${originalIng.name} for ${swappedFor.name}`);
            }
        });

        const combinedTweaks = [automaticTweaks.join('; '), trialData.tweaks].filter(Boolean).join('\n\n');
        form.append('tweaks', combinedTweaks);

        if (trialData.image) form.append('image', trialData.image);

        const res = await api.post(`/recipes/${recipe.id}/trials`, form);
        if (res.ok) {
            onHide();
            window.location.reload();
        } else {
            alert("Failed to save trial.");
        }
        setIsSaving(false);
    };

    const renderGlobalTimers = () => {
        if (activeTimers.length === 0) return null;
        return (
            <div className="d-flex flex-wrap gap-2 px-4 px-md-5 pt-3 justify-content-center">
                {activeTimers.map(t => {
                    const isDone = t.remaining === 0;
                    return (
                        <div key={t.id} className={`d-flex align-items-center rounded-pill shadow-sm border px-3 py-1 transition-all ${t.isRinging ? 'bg-danger text-white border-danger animate-pulse' : (isDone ? 'bg-light border-secondary text-muted' : 'bg-white border-primary-subtle')}`}>
                            <div className="fw-bold me-2" style={{fontVariantNumeric: 'tabular-nums'}}>{formatTime(t.remaining)}</div>
                            <div className={`small me-3 ${t.isRinging ? 'text-white-50' : 'text-muted'}`}>{t.label}</div>
                            <i className={`bi cursor-pointer me-2 fs-5 ${t.isRinging ? 'bi-stop-circle-fill text-white' : (t.isRunning ? 'bi-pause-circle' : (isDone ? 'bi-arrow-clockwise' : 'bi-play-circle'))}`} onClick={() => toggleTimer(t.id)}></i>
                            <i className="bi bi-x-circle cursor-pointer fs-5 opacity-75 hover-opacity-100" onClick={() => dismissTimer(t.id)}></i>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderMiseEnPlace = () => (
        <div className="animation-fade-in py-3">
            <div className="text-center mb-5 mx-auto" style={{ maxWidth: '800px' }}>
                <h2 className="fw-bold display-6 mb-2">Mise en Place</h2>
                <p className="text-muted fs-5 mb-1">Gather your ingredients and tools before you begin.</p>
            </div>

            <Row className="g-4 justify-content-center mx-auto" style={{ maxWidth: '1000px' }}>
                {recipe.ingredients && recipe.ingredients.length > 0 && (
                    <Col md={6}>
                        <div className="bg-surface rounded-4 p-4 shadow-sm border h-100">
                            <h6 className="fw-bold mb-3 text-primary text-header-caps"><i className="bi bi-basket me-2"></i> Ingredients to Gather</h6>
                            <div className="d-flex flex-column gap-3">
                                {recipe.ingredients.map(ing => {
                                    const isChecked = checkedItems.has(`ing_${ing.id}`);
                                    const activeIng = getActiveIngredient(ing);

                                    return (
                                        <div key={`ing_${ing.id}`} className={`p-3 rounded-3 border transition-all ${isChecked ? 'bg-light border-light opacity-50 text-muted' : 'bg-primary-subtle border-primary-subtle text-primary'}`}>
                                            <div className="d-flex justify-content-between align-items-center cursor-pointer" onClick={() => {
                                                const next = new Set(checkedItems);
                                                if (next.has(`ing_${ing.id}`)) next.delete(`ing_${ing.id}`); else next.add(`ing_${ing.id}`);
                                                setCheckedItems(next);
                                            }}>
                                                <div className="d-flex align-items-center">
                                                    <div className={`rounded-3 d-flex align-items-center justify-content-center me-3 shadow-sm transition-all ${isChecked ? 'bg-secondary text-white' : 'bg-white'}`} style={{width: '36px', height: '36px'}}>
                                                        <i className={`bi ${isChecked ? 'bi-check-lg' : 'bi-basket'}`}></i>
                                                    </div>
                                                    <span className={`fw-bold me-2 ${isChecked ? 'text-decoration-line-through' : ''}`} style={{minWidth: '70px'}}>
                                                        {activeIng.quantity ? decimalToFraction(activeIng.quantity) : ''} {activeIng.unit === 'Other' ? activeIng.custom_unit : activeIng.unit}
                                                    </span>
                                                    <span className={`fw-medium ${isChecked ? 'text-decoration-line-through' : ''}`}>{activeIng.name}</span>
                                                    {activeIng.notes && <span className="text-muted small ms-1 fst-italic">({activeIng.notes})</span>}
                                                </div>
                                                <i className={`bi fs-5 ms-2 transition-all ${isChecked ? 'bi-check-circle-fill text-secondary' : 'bi-circle text-primary opacity-50'}`}></i>
                                            </div>

                                            {ing.alternatives && ing.alternatives.length > 0 && (
                                                <div className="mt-3 ms-5 ps-2 border-start border-primary border-opacity-25" onClick={e => e.stopPropagation()}>
                                                    <Form.Select size="sm" className="bg-white border-0 shadow-sm text-primary fw-medium" value={swaps[ing.id]?.name || ''} onChange={e => {
                                                        const val = e.target.value;
                                                        if (val === '') {
                                                            const newSwaps = {...swaps};
                                                            delete newSwaps[ing.id];
                                                            setSwaps(newSwaps);
                                                        } else {
                                                            const selectedAlt = ing.alternatives.find(a => a.name === val);
                                                            setSwaps({...swaps, [ing.id]: selectedAlt});
                                                        }
                                                    }}>
                                                        <option value="">Original: {ing.name}</option>
                                                        {ing.alternatives.map((alt, idx) => (
                                                            <option key={idx} value={alt.name}>
                                                                Swap: {alt.name} ({alt.quantity ? decimalToFraction(alt.quantity) : ''} {alt.unit === 'Other' ? alt.custom_unit : alt.unit}{alt.notes ? `, ${alt.notes}` : ''})
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </Col>
                )}

                {recipe.equipment && recipe.equipment.length > 0 && (
                    <Col md={6}>
                        <div className="bg-surface rounded-4 p-4 shadow-sm border h-100">
                            <h6 className="fw-bold mb-3 text-secondary text-header-caps"><i className="bi bi-tools me-2"></i> Tools to Gather</h6>
                            <div className="d-flex flex-column gap-2">
                                {recipe.equipment.map(eq => {
                                    const isChecked = checkedItems.has(`eq_${eq.id}`);
                                    return (
                                        <div key={`eq_${eq.id}`} className={`d-flex justify-content-between align-items-center p-2 rounded-3 border transition-all cursor-pointer ${isChecked ? 'bg-light border-light opacity-50 text-muted' : 'bg-light border text-secondary hover-shadow-sm'}`} onClick={() => {
                                            const next = new Set(checkedItems);
                                            if (next.has(`eq_${eq.id}`)) next.delete(`eq_${eq.id}`); else next.add(`eq_${eq.id}`);
                                            setCheckedItems(next);
                                        }}>
                                            <div className="d-flex align-items-center">
                                                <div className={`rounded-3 d-flex align-items-center justify-content-center me-3 shadow-sm transition-all ${isChecked ? 'bg-secondary text-white' : 'bg-white'}`} style={{width: '36px', height: '36px'}}>
                                                    <i className={`bi ${isChecked ? 'bi-check-lg' : 'bi-tools'}`}></i>
                                                </div>
                                                <span className={`fw-medium ${isChecked ? 'text-decoration-line-through text-muted' : 'text-dark'}`}>{eq.name}</span>
                                            </div>
                                            <i className={`bi fs-5 ms-2 transition-all ${isChecked ? 'bi-check-circle-fill text-secondary' : 'bi-circle text-secondary opacity-50'}`}></i>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </Col>
                )}
            </Row>
        </div>
    );

    const renderStep = () => {
        const currentStep = recipe.steps[currentIndex - 1];
        if (!currentStep) return null;

        const hasInputs = currentStep.links && currentStep.links.length > 0;
        const hasOutputs = currentStep.outputs && currentStep.outputs.length > 0;
        const suggestions = extractTimeSuggestions(currentStep.instruction);

        return (
            <div className="animation-fade-in pb-5">
                <div className="mb-4 mx-auto" style={{ maxWidth: '800px' }}>
                    <div className="d-flex justify-content-center mb-4">
                        <Badge bg="primary" className="px-4 py-2 rounded-pill fs-6 fw-bold shadow-sm text-header-caps">
                            Step {currentStep.order_index} of {recipe.steps.length}
                        </Badge>
                    </div>
                    <div className="fs-4 lh-lg text-body-80 fw-medium text-start mx-auto px-3" style={{ whiteSpace: 'pre-wrap' }}>
                        {currentStep.instruction}
                    </div>
                </div>

                {suggestions.length > 0 && (
                    <div className="d-flex flex-wrap justify-content-center gap-2 mb-5 mx-auto" style={{ maxWidth: '800px' }}>
                        {suggestions.map((sug, idx) => (
                            <Button key={idx} variant="outline-primary" className="rounded-pill fw-bold d-flex align-items-center shadow-sm" onClick={() => startNewTimer(sug.label, sug.totalSeconds)}>
                                <i className="bi bi-play-fill fs-5 me-1"></i> Start {sug.label} Timer
                            </Button>
                        ))}
                    </div>
                )}

                {currentStep.image_url && (
                    <div className="text-center mb-5 mx-auto" style={{ maxWidth: '800px' }}>
                        <img src={currentStep.image_url} alt={`Step ${currentStep.order_index}`} className="img-fluid rounded-4 shadow-sm border" style={{ maxHeight: '400px', objectFit: 'cover', width: '100%' }} />
                    </div>
                )}

                <Row className="g-4 justify-content-center mx-auto" style={{ maxWidth: '1000px' }}>
                    {hasInputs && (
                        <Col md={hasOutputs ? 6 : 8} lg={hasOutputs ? 6 : 8}>
                            <div className="bg-surface rounded-4 p-4 shadow-sm border h-100">
                                <h6 className="fw-bold mb-3 text-muted text-header-caps"><i className="bi bi-arrow-down-right-square me-2"></i> Used in this step</h6>
                                <div className="d-flex flex-column gap-2">
                                    {currentStep.links.filter(l => l.ingredient_id).map((link, idx) => {
                                        const ing = getIngredientDetails(link.ingredient_id);
                                        if (!ing) return null;

                                        // --- NEW: DYNAMIC USAGE TEXT ---
                                        const isSwapped = !!swaps[link.ingredient_id];
                                        let displayUsage = link.usage_text;

                                        if (isSwapped) {
                                            // If swapped, ignore the hardcoded usage text and use the alternative's exact metrics
                                            const qtyStr = ing.quantity ? decimalToFraction(ing.quantity) : '';
                                            const unitStr = ing.unit === 'Other' ? (ing.custom_unit || '') : (ing.unit || '');
                                            displayUsage = `${qtyStr} ${unitStr}`.trim();
                                        }

                                        return (
                                            <div key={`ing_${idx}`} className="d-flex align-items-center p-2 rounded-3 bg-primary-subtle border border-primary-subtle text-primary">
                                                <div className="bg-white rounded-3 d-flex align-items-center justify-content-center me-3 shadow-sm" style={{width: '36px', height: '36px'}}>
                                                    <i className="bi bi-basket"></i>
                                                </div>
                                                <span className="fw-bold me-2" style={{minWidth: '70px'}}>{displayUsage}</span>
                                                <span className="fw-medium">
                                                    {ing.name}
                                                    {ing.notes && <span className="fst-italic small ms-1 text-primary text-opacity-75">({ing.notes})</span>}
                                                </span>
                                            </div>
                                        );
                                    })}

                                    {currentStep.links.filter(l => l.linked_output_id).map((link, idx) => {
                                        const comp = getComponentDetails(link.linked_output_id);
                                        if (!comp) return null;
                                        return (
                                            <div key={`comp_${idx}`} className="d-flex align-items-center p-2 rounded-3 border" style={{backgroundColor: '#f3e8ff', borderColor: '#e9d5ff', color: '#6b21a8'}}>
                                                <div className="bg-white rounded-3 d-flex align-items-center justify-content-center me-3 shadow-sm" style={{width: '36px', height: '36px'}}><i className="bi bi-box-seam"></i></div>
                                                <span className="fw-bold me-2" style={{minWidth: '70px'}}>{link.usage_text}</span>
                                                <span className="fw-medium">{comp.name}</span>
                                            </div>
                                        );
                                    })}

                                    {currentStep.links.filter(l => l.equipment_id).map((link, idx) => {
                                        const eq = getEquipmentDetails(link.equipment_id);
                                        if (!eq) return null;
                                        return (
                                            <div key={`eq_${idx}`} className="d-flex align-items-center p-2 rounded-3 bg-light border text-secondary">
                                                <div className="bg-white rounded-3 d-flex align-items-center justify-content-center me-3 shadow-sm" style={{width: '36px', height: '36px'}}><i className="bi bi-tools"></i></div>
                                                <span className="fw-bold me-2" style={{minWidth: '70px'}}>{link.usage_text || 'Use'}</span>
                                                <span className="fw-medium text-dark">{eq.name}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </Col>
                    )}

                    {hasOutputs && (
                        <Col md={hasInputs ? 6 : 8} lg={hasInputs ? 6 : 8}>
                            <div className="bg-surface rounded-4 p-4 shadow-sm border h-100">
                                <h6 className="fw-bold mb-3 text-success text-header-caps"><i className="bi bi-stars me-2"></i> This step yields</h6>
                                <div className="d-flex flex-column gap-2">
                                    {currentStep.outputs.map((out, idx) => (
                                        <div key={`out_${idx}`} className="d-flex align-items-center p-2 rounded-3 bg-success-subtle border border-success-subtle text-success">
                                            <div className="bg-white rounded-3 d-flex align-items-center justify-content-center me-3 shadow-sm" style={{width: '36px', height: '36px'}}><i className="bi bi-check-circle"></i></div>
                                            <span className="fw-bold me-2" style={{minWidth: '70px'}}>{out.quantity ? decimalToFraction(out.quantity) : ''} {out.unit}</span>
                                            <span className="fw-medium">{out.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Col>
                    )}
                </Row>
            </div>
        );
    };

    const renderTrialLog = () => (
        <div className="animation-fade-in py-5 mx-auto" style={{ maxWidth: '600px' }}>
            <div className="text-center mb-4">
                <div className="d-inline-flex align-items-center justify-content-center bg-success text-white rounded-circle shadow-sm mb-3" style={{width: '64px', height: '64px'}}>
                    <i className="bi bi-stars fs-2"></i>
                </div>
                <h2 className="fw-bold display-6">All Done!</h2>
                <p className="text-muted fs-5">How did it turn out? Log your results for next time.</p>
            </div>

            <div className="bg-surface rounded-4 p-4 p-md-5 shadow-sm border">
                <div className="text-center mb-4 pb-4 border-bottom">
                    <h6 className="text-header-caps text-muted mb-3">Rate this Trial</h6>
                    <div className="d-flex justify-content-center gap-2">
                        {[1, 2, 3, 4, 5].map(star => (
                            <i
                                key={star}
                                className={`bi fs-1 cursor-pointer transition-all ${trialData.rating >= star ? 'bi-star-fill text-warning' : 'bi-star text-muted opacity-50'}`}
                                onClick={() => setTrialData({...trialData, rating: star})}
                                style={{ transform: trialData.rating === star ? 'scale(1.1)' : 'scale(1)' }}
                            ></i>
                        ))}
                    </div>
                </div>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bold small text-muted text-header-caps">Did you tweak anything?</Form.Label>
                    <Form.Control as="textarea" rows={2} placeholder="e.g., Baked for 5 extra minutes, doubled the garlic..." value={trialData.tweaks} onChange={e => setTrialData({...trialData, tweaks: e.target.value})} />
                </Form.Group>

                <Form.Group className="mb-4">
                    <Form.Label className="fw-bold small text-muted text-header-caps">General Notes</Form.Label>
                    <Form.Control as="textarea" rows={3} placeholder="Texture was great, a bit too salty. Next time use less soy sauce." value={trialData.notes} onChange={e => setTrialData({...trialData, notes: e.target.value})} />
                </Form.Group>

                <Form.Group className="mb-2">
                    <Form.Label className="fw-bold small text-muted text-header-caps"><i className="bi bi-camera me-1"></i> Add a Photo</Form.Label>
                    <Form.Control type="file" accept="image/*" onChange={e => setTrialData({...trialData, image: e.target.files[0]})} />
                </Form.Group>
            </div>
        </div>
    );

    return (
        <Modal show={show} onHide={onHide} fullscreen animation={false} className="cooking-mode-modal">
            <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4 px-md-5 align-items-start">
                <Modal.Title className="fw-bold text-muted fs-6 text-header-caps mt-2">
                    {recipe.name} {currentIndex === 0 ? '• Mise en place' : (currentIndex === totalPages - 1 ? '• Review' : `• Step ${currentIndex} of ${recipe.steps?.length}`)}
                </Modal.Title>
                <div className="flex-grow-1">{renderGlobalTimers()}</div>
            </Modal.Header>

            <div className="px-4 px-md-5 pt-3">
                <ProgressBar now={progress} style={{ height: '6px' }} className="rounded-pill bg-light" variant={currentIndex === totalPages - 1 ? 'success' : 'primary'} />
            </div>

            <Modal.Body className="p-4 p-md-5 d-flex flex-column" style={{ overflowY: 'auto' }}>
                <div className="container-fluid max-w-1200 mx-auto flex-grow-1">
                    {currentIndex === 0 ? renderMiseEnPlace() : (currentIndex === totalPages - 1 ? renderTrialLog() : renderStep())}
                </div>
            </Modal.Body>

            <Modal.Footer className="border-0 p-4 p-md-5 bg-white sticky-bottom shadow-lg-top">
                <div className="container-fluid max-w-1200 mx-auto d-flex justify-content-between align-items-center px-0">
                    <Button variant="light" size="lg" className="rounded-pill px-4 px-md-5 fw-bold border" onClick={handlePrev} disabled={currentIndex === 0}>
                        <i className="bi bi-arrow-left me-2"></i> Back
                    </Button>

                    {currentIndex === totalPages - 1 ? (
                        <Button variant="success" size="lg" className="rounded-pill px-4 px-md-5 fw-bold shadow-sm" onClick={handleSaveTrial} disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save to Journal'} <i className="bi bi-check2-circle ms-1"></i>
                        </Button>
                    ) : (
                        <Button variant="primary" size="lg" className="rounded-pill px-4 px-md-5 fw-bold shadow-sm" onClick={handleNext}>
                            {currentIndex === 0 ? 'Start Cooking' : 'Next'} <i className="bi bi-arrow-right ms-2"></i>
                        </Button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
}
